// Shared domain types for the CoinfallSMP media system.
// Mirrors the D1 schema in apps/media-api/src/db/schema.sql — keep in sync.

export type VideoStatus = "draft" | "processing" | "published" | "failed";
export type VideoSourceType = "upload" | "youtube";

export interface Video {
  id: string;
  title: string;
  description: string | null;
  status: VideoStatus;
  sourceType: VideoSourceType;
  sourceUrl: string | null;
  r2Key: string | null;
  r2ThumbnailKey: string | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  fileSizeBytes: number | null;
  mimeType: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  viewsCount: number;
  likesCount: number;
  uploaderCreatorId: string | null;
}

/**
 * Computed, never stored directly: 'official' is reserved for exactly one
 * account (the CoinfallSMP channel itself, crown badge); 'verified' is any
 * other creator the admin has checked off (checkmark badge); null is an
 * unbadged approved creator.
 */
export type CreatorBadge = "official" | "verified" | null;

export function creatorBadge(creator: {
  isOfficial: boolean;
  isVerified: boolean;
}): CreatorBadge {
  if (creator.isOfficial) return "official";
  if (creator.isVerified) return "verified";
  return null;
}

/** Minimal uploader info embedded in video responses — never the creator's email. */
export interface UploaderSummary {
  id: string;
  username: string;
  displayName: string;
  /** Fully resolved image URL — an external link, our avatar proxy for an uploaded one, or null. */
  avatarUrl: string | null;
  badge: CreatorBadge;
}

/** Shape returned by the public API — never leaks internal R2 keys or admin fields. */
export interface PublicVideo {
  id: string;
  title: string;
  description: string | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  sourceType: VideoSourceType;
  sourceUrl: string | null;
  publishedAt: string;
  /** Fully resolved image URL — YouTube's own CDN for embeds, our thumbnail proxy for uploads, or null. */
  thumbnailUrl: string | null;
  viewsCount: number;
  likesCount: number;
  uploader: UploaderSummary | null;
}

export type CreatorStatus = "pending" | "approved" | "revoked";

/**
 * Full admin-facing account record — includes the email tied to their login,
 * but NEVER a password or password hash; that never leaves the server in
 * any shape. Admins are accounts in this same table with isAdmin=true, not a
 * separate concept — see the "Accounts and login" README section.
 */
export interface Creator {
  id: string;
  username: string;
  displayName: string;
  email: string;
  bio: string | null;
  /** Set via a real file upload (needs R2) — see also avatarUrl for a linked external image. */
  avatarKey: string | null;
  /** Set by pasting a link to an image hosted elsewhere — no R2 needed. avatarKey takes priority when both are set. */
  avatarUrl: string | null;
  status: CreatorStatus;
  canUpload: boolean;
  isVerified: boolean;
  isOfficial: boolean;
  isAdmin: boolean;
  isDisabled: boolean;
  mustResetPassword: boolean;
  hasPassword: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Public creator profile page payload — no email, adds aggregate stats + their videos. */
export interface PublicCreatorProfile {
  id: string;
  username: string;
  displayName: string;
  bio: string | null;
  avatarUrl: string | null;
  badge: CreatorBadge;
  uploadCount: number;
  totalViews: number;
  videos: PublicVideo[];
}

/**
 * What GET /api/auth/me resolves the current session (or lack of one) to.
 * There is no implicit "unknown visitor = admin" case — being logged out,
 * or logged in with no special flags, both mean no elevated access at all.
 */
export type AuthIdentity =
  | { loggedIn: false }
  | { loggedIn: true; creator: Creator };

export type CreatorApplicationStatus =
  | "pending"
  | "needs_info"
  | "approved"
  | "rejected";

export interface CreatorApplication {
  id: string;
  requestedUsername: string;
  displayName: string;
  email: string;
  youtubeUrl: string | null;
  minecraftUsername: string | null;
  discordHandle: string | null;
  reason: string;
  otherLinks: string | null;
  additionalInfo: string | null;
  status: CreatorApplicationStatus;
  reviewNotes: string | null;
  reviewedBy: string | null;
  resultingCreatorId: string | null;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
}

export type PublishPlatform = "website" | "tiktok" | "instagram";
export type PublishTargetStatus =
  | "not_started"
  | "queued"
  | "publishing"
  | "published"
  | "failed";

export interface PublishTarget {
  id: string;
  videoId: string;
  platform: PublishPlatform;
  status: PublishTargetStatus;
  externalUrl: string | null;
  externalId: string | null;
  errorMessage: string | null;
  updatedAt: string;
}

export type JobType = "youtube_import" | "transcode" | "thumbnail_extract";
export type JobStatus = "queued" | "running" | "succeeded" | "failed";

export interface Job {
  id: string;
  type: JobType;
  videoId: string | null;
  status: JobStatus;
  progressPct: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminVideoDetail extends Video {
  publishTargets: PublishTarget[];
  jobs: Job[];
  uploader: UploaderSummary | null;
}

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * Whether this deployment has R2 configured. When false, direct file
 * uploads and avatar uploads are unavailable — YouTube-embedded videos are
 * unaffected, since they never touch object storage at all.
 */
export interface AppConfig {
  uploadsEnabled: boolean;
}
